import { useCallback, useEffect, useRef, useState } from 'react';
import { acquire, release } from '../api/imageBlobCache';

export type AuthImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface Options {
  /** Defer loading until the element scrolls near the viewport. */
  lazy?: boolean;
  /** IntersectionObserver rootMargin for lazy loading. */
  rootMargin?: string;
}

interface Result {
  /** Blob URL once loaded, otherwise null. */
  src: string | null;
  status: AuthImageStatus;
  /** Attach to the element whose visibility gates a lazy load. */
  ref: (node: Element | null) => void;
}

/**
 * Load an ownership-checked / encrypted image via authenticated fetch and
 * expose it as a blob: URL. Reference-counted through the shared blob cache:
 * the same path is fetched once and the blob is kept alive while any consumer
 * is mounted.
 */
export function useAuthImage(path: string | null | undefined, opts: Options = {}): Result {
  const { lazy = false, rootMargin = '200px' } = opts;

  const [src, setSrc] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthImageStatus>('idle');
  const [visible, setVisible] = useState(!lazy);

  const observerRef = useRef<IntersectionObserver | null>(null);

  // Lazy gate: observe the element and flip `visible` once it nears the viewport.
  const ref = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!lazy || !node || typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setVisible(true);
            observer.disconnect();
            observerRef.current = null;
          }
        },
        { rootMargin },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [lazy, rootMargin],
  );

  useEffect(() => {
    if (!path || !visible) {
      setSrc(null);
      setStatus(path ? 'idle' : 'idle');
      return;
    }

    let active = true;
    const controller = new AbortController();
    setStatus('loading');
    setSrc(null);

    void acquire(path, controller.signal).then(
      (url) => {
        if (active) {
          setSrc(url);
          setStatus('loaded');
        }
      },
      (error: unknown) => {
        if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
          setStatus('error');
        }
      },
    );

    return () => {
      active = false;
      controller.abort();
      release(path);
    };
  }, [path, visible]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { src, status, ref };
}
